import { Color, Icon, Tooltip, useModal, withModalConditionalRender } from '@billy/design-system'
import { Global } from '@emotion/core'
import { Menu, SplitButton } from '@shinetools/lumen-react'
import React, { ReactElement, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DISPUTE_I18N_KEYS, EInvoiceStatus, REFUSE_I18N_KEYS, SUSPEND_I18N_KEYS } from '@features/eInvoicing/constants'
import { LifecycleActionModal } from '@features/eInvoicing/elements/LifecycleActionModal'
import { EInvoiceLifecycleTrackingEvents, type EInvoiceLifecycleTrackingSource } from '@views/receipts/enums/tracking'

import { ModalId } from '../../../../enums/modalId'
import { useSegment } from '../../../../hooks'
import * as Styled from './styles'

const LifecycleModal = withModalConditionalRender(LifecycleActionModal)

interface EInvoiceLifecycleActionsProps {
  attachmentId?: string
  disabled?: boolean
  fullWidth?: boolean
  onProcessingChange?: (isProcessing: boolean) => void
  onSuccess?: () => void
  previousStatus?: EInvoiceStatus | null
  trackingSource: EInvoiceLifecycleTrackingSource
}

export const EInvoiceLifecycleActions = ({
  attachmentId,
  disabled = false,
  fullWidth = false,
  onProcessingChange,
  onSuccess,
  previousStatus,
  trackingSource,
}: EInvoiceLifecycleActionsProps): ReactElement | null => {
  const [isDisputeButtonHovered, setIsDisputeButtonHovered] = useState(false)
  const [isLifecycleActionProcessing, setIsLifecycleActionProcessing] = useState(false)
  const disputeButtonRef = useRef<HTMLButtonElement>(null)

  const { t } = useTranslation()
  const { open: openRefuseModal } = useModal(ModalId.EInvoiceRejectionModal)
  const { open: openDisputeModal } = useModal(ModalId.EInvoiceDisputeModal)
  const { open: openSuspendModal } = useModal(ModalId.EInvoiceSuspendModal)

  const { track } = useSegment()

  const handleLifecycleProcessingChange = useCallback(
    (isProcessing: boolean) => {
      setIsLifecycleActionProcessing(isProcessing)
      onProcessingChange?.(isProcessing)
    },
    [onProcessingChange],
  )

  const handleDisputeClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isLifecycleActionProcessing) {
        return
      }

      event.stopPropagation()
      track(EInvoiceLifecycleTrackingEvents.DisputeCTAClicked, { source: trackingSource })
      openDisputeModal()
    },
    [isLifecycleActionProcessing, openDisputeModal, track, trackingSource],
  )

  const handleSuspendClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isLifecycleActionProcessing) {
        return
      }

      event.stopPropagation()
      track(EInvoiceLifecycleTrackingEvents.SuspendCTAClicked, { source: trackingSource })
      openSuspendModal()
    },
    [isLifecycleActionProcessing, openSuspendModal, track, trackingSource],
  )

  const handleRefuseClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isLifecycleActionProcessing) {
        return
      }

      event.stopPropagation()
      track(EInvoiceLifecycleTrackingEvents.RefuseCTAClicked, { source: trackingSource })
      openRefuseModal()
    },
    [isLifecycleActionProcessing, openRefuseModal, track, trackingSource],
  )

  // Workaround to add a tooltip to the SplitButton's main CTA, currently missing in the component
  const handleDisputeButtonMouseOver = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const isHoveringMainButton = disputeButtonRef.current?.contains(e.target as Node) ?? false
    setIsDisputeButtonHovered(isHoveringMainButton)
  }, [])

  const handleDisputeButtonMouseLeave = useCallback(() => {
    setIsDisputeButtonHovered(false)
  }, [])

  // The API rejects dispute, suspend and refuse for an approved e-invoice
  if (!attachmentId || previousStatus === EInvoiceStatus.Approved) {
    return null
  }

  const splitButton = (
    <Tooltip forceHidden={!isDisputeButtonHovered} label={t('bill.action.dispute_tooltip')} placement="bottom">
      <SplitButton
        disabled={disabled || isLifecycleActionProcessing}
        label={t('bill.action.dispute')}
        menu={
          <>
            <Menu.Item
              icon={<Icon icon="pause" color={Color.Black} />}
              label={t('bill.action.suspend_menu_label')}
              onClick={handleSuspendClick}
            />
            <Menu.Item
              icon={<Icon icon="circleX" color={Color.Red700} />}
              label={t('bill.action.refuse_menu_label')}
              onClick={handleRefuseClick}
            />
          </>
        }
        menuLabel={t('bill.action.other_actions')}
        menuSize="medium"
        onClick={handleDisputeClick}
        onMouseLeave={handleDisputeButtonMouseLeave}
        onMouseOver={handleDisputeButtonMouseOver}
        ref={disputeButtonRef}
        startIcon={<Icon icon="circleAlert" color={Color.Black} />}
        variant="outlined"
      />
    </Tooltip>
  )

  return (
    <>
      <Global styles={Styled.lumenMenuStackingFix} />
      {fullWidth ? <Styled.FullWidthSplitButtonWrapper>{splitButton}</Styled.FullWidthSplitButtonWrapper> : splitButton}

      <LifecycleModal
        action="refuse"
        attachmentId={attachmentId}
        buttonColor="danger"
        i18nKeys={REFUSE_I18N_KEYS}
        id={ModalId.EInvoiceRejectionModal}
        onProcessingChange={handleLifecycleProcessingChange}
        onSuccess={onSuccess}
        previousStatus={previousStatus}
        trackingSource={trackingSource}
      />
      <LifecycleModal
        action="dispute"
        attachmentId={attachmentId}
        i18nKeys={DISPUTE_I18N_KEYS}
        id={ModalId.EInvoiceDisputeModal}
        onProcessingChange={handleLifecycleProcessingChange}
        onSuccess={onSuccess}
        previousStatus={previousStatus}
        trackingSource={trackingSource}
      />
      <LifecycleModal
        action="suspend"
        attachmentId={attachmentId}
        i18nKeys={SUSPEND_I18N_KEYS}
        id={ModalId.EInvoiceSuspendModal}
        onProcessingChange={handleLifecycleProcessingChange}
        onSuccess={onSuccess}
        previousStatus={previousStatus}
        trackingSource={trackingSource}
      />
    </>
  )
}
